import React, { useEffect, useState, useMemo } from "react";
import { useDraggable } from "@dnd-kit/core";
import Fuse from "fuse.js";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { ColumnContextMenu, ColumnContextInfo } from "./ColumnContextMenu";
import { SchemaColumn, SchemaTable } from "./types";
import { useReportBuilderStore } from "./store";
import { DATA_TYPE_CONFIG } from "./constants";

// ─── Data type badge ──────────────────────────────────────────────────────────

function DataTypeBadge({ type }: { type: SchemaColumn["dataType"] }) {
  const cfg = DATA_TYPE_CONFIG[type] ?? DATA_TYPE_CONFIG.string;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border} leading-none`}
    >
      {cfg.label}
    </span>
  );
}

// ─── Draggable column row ─────────────────────────────────────────────────────

function DraggableColumnRow({
  col,
  tableId,
  onAdd,
}: {
  col: SchemaColumn;
  tableId: string;
  onAdd: (col: SchemaColumn) => void;
}) {
  const { config, activeSheetId } = useReportBuilderStore();
  const activeSheet =
    config.sheets.find((s) => s.id === activeSheetId) ?? config.sheets[0];
  const isAdded = activeSheet.columns.some(
    (c) => c.sourceTable === col.sourceTable && c.sourceColumn === col.name
  );
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `schema_${col.id}`,
    data: { type: "schema-column", col },
  });

  const ctxInfo: ColumnContextInfo = {
    displayName: col.displayName,
    dataType: col.dataType,
    description: col.description,
    sourceTable: col.sourceTable,
    sourceColumn: col.name,
    isCustom: false,
  };

  return (
    <>
      {ctxMenu && (
        <ColumnContextMenu
          info={ctxInfo}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
        />
      )}
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onContextMenu={(e) => {
          e.preventDefault();
          setCtxMenu({ x: e.clientX, y: e.clientY });
        }}
        className={`group flex items-center gap-2 px-3 py-1.5 rounded-md cursor-grab hover:bg-gray-50 transition-colors ${
          isDragging ? "opacity-50" : ""
        } ${isAdded ? "opacity-60" : ""}`}
        title={col.description}
      >
        {/* Drag handle icon */}
        <svg
          className="w-3 h-3 text-gray-300 group-hover:text-gray-400 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M8 6h2v2H8zm0 4h2v2H8zm0 4h2v2H8zm6-8h2v2h-2zm0 4h2v2h-2zm0 4h2v2h-2z" />
        </svg>

        {/* Column name + description */}
        <div className="flex-1 min-w-0">
          <span className="block text-xs text-gray-700 truncate leading-snug">
            {col.displayName}
          </span>
          {col.description && col.description !== col.displayName && (
            <span className="block text-[10px] text-gray-400 truncate leading-snug">
              {col.description}
            </span>
          )}
        </div>

        {/* Data type badge */}
        <DataTypeBadge type={col.dataType} />

        {/* Add button */}
        {!isAdded && (
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onAdd(col);
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            title="Add to report"
          >
            <PlusIcon className="w-3.5 h-3.5 text-primary hover:text-secondary" />
          </button>
        )}

        {isAdded && (
          <span className="text-[10px] text-green-600 font-medium">Added</span>
        )}
      </div>
    </>
  );
}

// ─── Table group ──────────────────────────────────────────────────────────────

function TableGroup({
  table,
  defaultOpen = false,
  highlightIds,
  onAddCol,
}: {
  table: SchemaTable;
  defaultOpen?: boolean;
  highlightIds: Set<string>;
  onAddCol: (col: SchemaColumn) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);

  const visibleCols = table.columns.filter(
    (c) => highlightIds.size === 0 || highlightIds.has(c.id)
  );
  const hasFilteredMatch =
    highlightIds.size > 0 && table.columns.some((c) => highlightIds.has(c.id));

  // When someone searches, reveal the matching fields instead of leaving the
  // relevant table collapsed.
  useEffect(() => {
    if (hasFilteredMatch) setOpen(true);
  }, [hasFilteredMatch]);

  if (highlightIds.size > 0 && visibleCols.length === 0) return null;

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1.5 px-2 py-2 rounded-md hover:bg-gray-50 transition-colors text-left"
      >
        {open ? (
          <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-gray-800 truncate">
            {table.displayName}
          </div>
        </div>

        <span className="text-[10px] text-gray-400 flex-shrink-0">
          {visibleCols.length}
        </span>
      </button>

      {open && (
        <div className="ml-2 border-l border-gray-100 pl-1">
          {visibleCols.map((col) => (
            <DraggableColumnRow
              key={col.id}
              col={col}
              tableId={table.id}
              onAdd={onAddCol}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Schema Browser ──────────────────────────────────────────────────────

export function SchemaBrowser() {
  const { schema, addColumn, isSchemaCollapsed, toggleSchemaCollapsed } =
    useReportBuilderStore();

  const [searchQuery, setSearchQuery] = useState("");

  // Flatten all columns for Fuse search
  const allColumns = useMemo(() => schema.flatMap((t) => t.columns), [schema]);

  const fuse = useMemo(
    () =>
      new Fuse(allColumns, {
        keys: ["displayName", "name", "description", "sourceTable"],
        threshold: 0.35,
      }),
    [allColumns]
  );

  const matchingIds = useMemo<Set<string>>(() => {
    let cols = allColumns;

    if (searchQuery.trim()) {
      const results = fuse.search(searchQuery.trim());
      const resultIds = new Set(results.map((r) => r.item.id));
      cols = cols.filter((c) => resultIds.has(c.id));
    }

    if (!searchQuery.trim()) return new Set();
    return new Set(cols.map((c) => c.id));
  }, [searchQuery, allColumns, fuse]);

  const trimmedSearchQuery = searchQuery.trim();
  const isFiltering = Boolean(trimmedSearchQuery);
  const hasNoResults = isFiltering && matchingIds.size === 0;

  if (isSchemaCollapsed) {
    return (
      <div className="w-10 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-2">
        <button
          onClick={toggleSchemaCollapsed}
          title="Expand schema browser"
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
        >
          <ChevronRightIcon className="w-4 h-4 text-gray-500" />
        </button>
        <div
          className="text-[10px] text-gray-400 font-semibold"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          SCHEMA
        </div>
      </div>
    );
  }

  return (
    <div
      data-tour="report-fields"
      className="w-72 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col h-full"
    >
      {/* Header */}
      <div className="px-3 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Schema Browser
          </h2>
          <button
            onClick={toggleSchemaCollapsed}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            title="Collapse"
          >
            <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400 rotate-90" />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search columns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>

        <div className="flex items-center gap-2 mt-2">
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
              }}
              className="text-[11px] text-gray-400 hover:text-gray-600"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table list */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {hasNoResults ? (
          <div className="flex flex-col items-center px-5 py-10 text-center">
            <MagnifyingGlassIcon className="h-6 w-6 text-gray-300" />
            <p className="mt-2 text-xs font-medium text-gray-600">
              No matching fields found
            </p>
            <p className="mt-1 text-[11px] leading-4 text-gray-400">
              Try a different keyword or clear the search.
            </p>
            <button
              onClick={() => {
                setSearchQuery("");
              }}
              className="mt-3 text-xs font-medium text-primary hover:text-primaryHover"
            >
              Clear filters
            </button>
          </div>
        ) : (
          schema.map((table) => (
            <TableGroup
              key={table.id}
              table={table}
              defaultOpen={schema.indexOf(table) === 0}
              highlightIds={matchingIds}
              onAddCol={addColumn}
            />
          ))
        )}
      </div>
    </div>
  );
}
